/**
 * useCardStep — what the card step does: the one saved attempt, both provider return landings,
 * and completion (E23.9 FR3, FR4, FR7, FR8). PaymentStep renders it; the copy for each `notice`
 * lives there, so this hook states facts and never sentences.
 *
 * **The attempt is kept in sessionStorage**, because S3's return URLs carry no session id by
 * design (backend/app/routes/payment_setup.py `_return_urls`) and a full-page redirect loses
 * every piece of component state. The step checks this tab can keep one before it asks for a
 * provider session, and writes it immediately before the redirect, which does not happen if the
 * write fails. It holds
 * `{ setup_session_id, idempotency_key, returned, owner }`. It is cleared on completion, on a
 * cancelled return, on the step's own logout, and when a different account reads it (`owner`).
 * It SURVIVES the 401 sign-out of an expired registration token, which is the case FR7 needs it
 * for: completion resumes after the customer signs back in. Per tab, because an attempt belongs
 * to the tab that opened it.
 *
 * **The live standing decides, not the landing.** `?status=cancelled` proves only that the
 * browser came back, so whenever a card may be on file the hook reads `GET /users/me` before it
 * says anything, and a tenant that is ready already ends in a full session or an honest sign-in
 * (FR8), never in a step the customer has finished.
 *
 * Callbacks: `onComplete(tokenResponse)` with the full session to commit, `onCardOnFile()` to
 * sign out with the card-on-file notice, `onLogout()` for the step's logout. `redirect` is the
 * navigation to the provider, a parameter only so tests can observe it.
 */

import { useEffect, useRef, useState } from "react";
import * as api from "../engine/api";
import { getSessionKey } from "../engine/auth";
import { CONSENT_VERSION } from "../engine/paymentConsent";

// S3's two return paths. Routerless, like /verify-email: read once from the URL, then scrubbed.
const COMPLETE_PATH = "/registration/complete";
const CARD_PATH = "/registration/card";

const ATTEMPT_KEY = "omega_card_attempt";
const PROBE_KEY = "omega_card_attempt_probe";

/** Which provider return this page load is, if any: "complete", "cancelled" or null. */
function readLanding() {
  if (typeof window === "undefined" || !window.location) return null;
  const { pathname, search } = window.location;
  if (pathname === COMPLETE_PATH) return "complete";
  if (pathname === CARD_PATH && new URLSearchParams(search).get("status") === "cancelled") return "cancelled";
  return null;
}

function scrubLanding() {
  if (typeof window !== "undefined" && window.history && window.history.replaceState) {
    window.history.replaceState({}, "", "/");
  }
}

/** The saved attempt, if this account owns it. Another account's is dropped, never used. */
function readAttempt(owner) {
  let attempt = null;
  try {
    attempt = JSON.parse(sessionStorage.getItem(ATTEMPT_KEY) || "null");
  } catch {
    attempt = null;
  }
  if (!attempt || typeof attempt.setup_session_id !== "string") return null;
  if (attempt.owner !== owner) {
    clearAttempt();
    return null;
  }
  return attempt;
}

/** Save the attempt; false when the browser refused to keep it. */
function saveAttempt(attempt) {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(attempt));
    return true;
  } catch {
    return false;
  }
}

/** Whether this tab can keep an attempt at all. Its own key, so a kept attempt is never touched. */
function canKeepAttempt() {
  try {
    sessionStorage.setItem(PROBE_KEY, "1");
    sessionStorage.removeItem(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
}

function clearAttempt() {
  try {
    sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    // Nothing stored, or nothing that can be.
  }
}

const isUnavailable = (err) => err?.status === 502 || err?.status === 503;

export default function useCardStep({
  onComplete,
  onCardOnFile,
  onLogout,
  redirect = (url) => window.location.assign(url),
}) {
  // Read once: StrictMode's second effect run, and any later render, must see the same landing
  // the page loaded on, after the URL has been scrubbed.
  const [landing] = useState(readLanding);
  const [phase, setPhase] = useState("checking");
  const [notice, setNotice] = useState(null);

  // The latest callbacks, so a flow awaiting the network calls the current ones.
  const callbacks = useRef({});
  callbacks.current = { onComplete, onCardOnFile, onLogout, redirect };

  // `live` is false once the step has unmounted, which is when a late result must do nothing: a
  // completion landing after the customer signed out must not sign them back in, and no result
  // arriving after an await may write the stored attempt unless it is still live. `started` keeps
  // the mount flow to one run, so StrictMode's simulated remount does not complete twice.
  const live = useRef(true);
  const started = useRef(false);

  const owner = () => getSessionKey(api.getToken());
  // The account this step was mounted for. App remounts the step when the stored token becomes
  // another account's, but only once this tab handles `storage`, which the browser queues as a
  // task of its own: a response can land first. So a result also checks the token stored NOW.
  // By account, not by token: signing in again as the same account keeps App's key, so nothing
  // would remount a step that dropped its result for that.
  const [account] = useState(owner);
  const current = () => live.current && owner() === account;

  const settleReady = (next) => {
    if (!current()) return;
    setNotice(next);
    setPhase("ready");
  };

  /** "complete", "pending", "signed_out" (a 401 the transport already acted on) or "unknown". */
  const readStanding = async () => {
    try {
      const profile = await api.getProfile();
      return profile?.registration_status === "complete" ? "complete" : "pending";
    } catch (err) {
      return err?.status === 401 ? "signed_out" : "unknown";
    }
  };

  // FR8 with no saved id: S3 hands a ready tenant no session id, deliberately, so there is
  // nothing to complete with and the honest end is a sign-in.
  const endWithCardOnFile = () => {
    if (!current()) return;
    clearAttempt();
    callbacks.current.onCardOnFile();
  };

  /** What a refused completion or creation means once the live standing is known. */
  const settleAfterRefusal = async (fallback) => {
    const standing = await readStanding();
    if (!current() || standing === "signed_out") return;
    if (standing === "complete") return endWithCardOnFile();
    settleReady(standing === "unknown" ? "error" : fallback);
  };

  const complete = async (attempt) => {
    if (current()) setPhase("completing");
    let resp;
    try {
      resp = await api.completeRegistration(attempt.setup_session_id);
    } catch (err) {
      // A refusal for a step that has gone, or whose account is no longer stored, touches nothing.
      // The attempt in storage may by now be a different account's, opened in this tab after this
      // one signed out.
      if (!current()) return;
      // Expired registration token: api.js has signed the customer out, and the attempt stays so
      // the completion resumes after they sign back in (FR7).
      if (err?.status === 401) return;
      // Readiness was not written, though a failed provider read marks the session failed. A
      // later completion reads the provider again regardless, so keep the attempt: retrying
      // completes the same session.
      if (isUnavailable(err)) return settleReady("unavailable");
      // 404 (not this account's), 422 (expired, not confirmed, not verifiable) and 409 (a card
      // from another session is on file) all mean THIS attempt cannot be completed.
      if (err?.status === 404 || err?.status === 409 || err?.status === 422) {
        clearAttempt();
        return settleAfterRefusal("not_completed");
      }
      return settleReady("error");
    }
    if (!current()) return;
    clearAttempt();
    callbacks.current.onComplete(resp);
  };

  const resolve = async (from) => {
    if (current()) setPhase("checking");
    let attempt = readAttempt(owner());
    if (from === "cancelled") {
      clearAttempt();
      attempt = null;
    }
    if (from === "complete" && attempt && !attempt.returned) {
      attempt = { ...attempt, returned: true };
      saveAttempt(attempt);
    }
    // FR7: the provider returned from this attempt, on this load or before a sign-in.
    if (attempt?.returned) return complete(attempt);

    const standing = await readStanding();
    if (!current() || standing === "signed_out") return;
    // FR8: ready already. Complete with the saved id if there is one; S3 answers the attempt
    // that made the tenant ready idempotently and refuses any other, which then signs out.
    if (standing === "complete") return attempt ? complete(attempt) : endWithCardOnFile();
    if (standing === "unknown") return settleReady("error");
    if (from === "cancelled") return settleReady("cancelled");
    if (from === "complete") return settleReady("returned_unconfirmed");
    return settleReady(attempt ? "abandoned" : null);
  };

  useEffect(() => {
    live.current = true;
    if (!started.current) {
      started.current = true;
      if (landing) scrubLanding();
      resolve(landing);
    }
    return () => {
      live.current = false;
    };
    // Mount only, by design: the landing is read once and the flow runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Open a NEW attempt and redirect to the provider. Every call sends a new idempotency key:
   *  reusing one replays the old session with `checkout_url: null` (E23.5 FR2). */
  const start = async () => {
    if (phase === "redirecting" || phase === "completing") return;
    // The return URLs carry no session id, so an attempt this tab cannot keep is one it could
    // never complete. Asked before the backend opens a provider session: a browser refusing now
    // would refuse every retry too, and each would open another.
    if (!canKeepAttempt()) return settleReady("storage_refused");
    const attemptOwner = owner();
    const idempotencyKey = globalThis.crypto.randomUUID();
    setNotice(null);
    setPhase("redirecting");
    let resp;
    try {
      resp = await api.createPaymentMethodSession({ consentVersion: CONSENT_VERSION, idempotencyKey });
    } catch (err) {
      if (err?.status === 401) return;
      // A stale version means this page predates the backend's consent; only a refresh fixes it.
      if (err?.status === 422 && err.code === "payment_consent_required" && err.details?.expected) {
        return settleReady("consent_outdated");
      }
      if (isUnavailable(err)) return settleReady("unavailable");
      if (err?.status === 409) return settleAfterRefusal("error");
      return settleReady("error");
    }
    if (!current()) return;
    // E23.5 FR6: a ready tenant is answered, not refused, and handed no session to complete.
    if (resp?.payment_method_status === "ready") return endWithCardOnFile();
    if (!resp?.checkout_url || !resp?.setup_session_id) return settleReady("error");
    const saved = saveAttempt({
      setup_session_id: resp.setup_session_id,
      idempotency_key: idempotencyKey,
      returned: false,
      owner: attemptOwner,
    });
    // Refused after the check passed: redirecting would have the customer enter a card at the
    // provider for nothing.
    if (!saved) return settleReady("storage_refused");
    callbacks.current.redirect(resp.checkout_url);
  };

  /** Run the standing check again: completes a kept attempt after an `unavailable`. */
  const retry = () => resolve(null);

  /** The step's logout: an explicit sign-out forgets the attempt; the 401 sign-out does not. */
  const logout = () => {
    clearAttempt();
    callbacks.current.onLogout();
  };

  return { phase, notice, start, retry, logout };
}
